import * as Tone from 'tone'
import { InstrumentBase } from './InstrumentBase';
import units from "../units.json"
import { getAudioGraphInstance } from '../audioEngine';
import { GrooveBoxUI } from "../projectViews/stationViews/GrooveBoxUI";
import { getSampleLibraryManagerInstance } from '../SamplePlayer';
import { findNoteInSequence, addKeysAsField, findNoteIndexInSequence } from '../utils/misc';
import { staticSelect } from '../utils/react';
import { limitToNyquist } from '../utils/parameters';
import colors from "../../styles/colors.module.scss";

// midiPitches are also constrained by pitchRange
const playerConfig = {
    openHat: {
        label: "Open Hat",
        midiPitch: 46, // A#2
        defaultSampleURL: "https://cdn.freesound.org/previews/509/509984_8033171-hq.mp3",
        color: colors.green,
    },
    closedHat: {
        label: "Closed Hat",
        midiPitch: 42, // F#2
        defaultSampleURL: "https://cdn.freesound.org/previews/173/173537_1372815-hq.mp3",
        color: colors.red,

    },
    snare: {
        label: "Snare",
        midiPitch: 38, // D2
        defaultSampleURL: "https://cdn.freesound.org/previews/561/561511_12517458-hq.mp3",
        color: colors.turquoise,
    },
    kick: {
        label: "Kick",
        midiPitch: 36, // C2
        defaultSampleURL: "https://cdn.freesound.org/previews/324/324982_3914271-hq.mp3",
        color: colors.yellow,
    }
}

export class GrooveBox extends InstrumentBase {

    static metadata = {
        tipus: 'groovebox',
        tipusDisplay: 'GrooveBox',
        versio: '0.1',
        color: colors.yellow,
        hasLogo: true,
        hasMiniature: true,
    }
    static parametersDescription = addKeysAsField({
        ...super.parametersDescription,
        lpf_cutoff: {type: 'float', unit: units.hertz, min: 200, max: 20000, initial: 20000, logarithmic: true, bind: "preset"},
        
        ...Object.fromEntries(
            Object.entries(playerConfig).flatMap(([name, {label}]) => {
                return [
                    [`${name}_swing`, { type: 'float', display: "percent", min: -.25, max: .25, initial: 0, bind: "preset" }],
                    [`${name}_tone`, { type: 'float', unit: units.semitone, min: -12, max: 12, step: 1, initial: 0, bind: "preset" }],
                    [`${name}_volume`, { type: 'float', unit: units.decibel, min: -30, max: 6, initial: -3, bind: "preset" }],
                    [`${name}_release`, { type: 'float', display: "percent", min: 0, max: 1, step: .01, initial: 1, bind: "preset" }],
                    [`${name}_reverbSend`, { type: 'float', unit: units.decibel, min: -30, max: 6, initial: -30, bind: "preset" }],
                    [`${name}_drumkit`, {type: 'enum', options: ['Hip Hop Classic 1', 'Hip Hop Classic 2', 'Reggae Roots', 'Dub Reggae', 'Soul Pop', 'Funky Soul', 'Acid House', 'Trap 1', 'Trap 2', 'Urban Reggaeton'], initial: 'Hip Hop Classic 1', bind: "preset"}],
                ]
            })
        ),
    }, "nom");
    static pitchRange = {
        ...super.pitchRange,
        usable: {min: 36, max: 46}
    }
    static defaultClipLength = 16;

    getUserInterfaceComponent() {
        return GrooveBoxUI
    }

    get playerNames() {
        return [...Object.keys(playerConfig)];
    }

    getPlayerPitch = playerName => playerConfig[playerName].midiPitch;
    getPlayerColor = playerName => playerConfig[playerName].color;
    getPlayerLabel = playerName => playerConfig[playerName].label;



    async buildInstrumentAudioGraph() {
        await super.buildInstrumentAudioGraph();
        const cutoff = new Tone.Filter(limitToNyquist(500), 'lowpass', -24);
        cutoff.set({'Q': 1.0,})  // Afegeix un punt de ressonància que li dona vidilla

        const mainChannel = new Tone.Channel({
            volume: 0
        }).connect(cutoff);

        const grooveBoxReverb = new Tone.Reverb({
            wet: 1,
            decay: 5,
        }).connect(this.bus);

        const grooveBoxReverbChannel = new Tone.Channel({
            volume: 0,
        }).connect(grooveBoxReverb);

        this.audioNodes = {mainChannel, cutoff}
        this.playerNames.forEach(playerName => {
            const reverbSend = new Tone.Gain(-100, "decibels").connect(grooveBoxReverbChannel);
            const player = new Tone.Player({ fadeIn: 0.01, fadeOut: 0.01 }).fan(mainChannel, reverbSend);
            Object.assign(this.audioNodes, {
                [`${playerName}reverbSend`]: reverbSend,
                [playerName]: player,
            });
        })
        this.addEffectChainNodes(cutoff, this.bus);

    }

    setParameterInAudioGraph(parameterName, value) {
        const parametersMatch = parameterName.match(/^(\w+)_(drumkit|volume|release|tone|reverbSend)$/);
        if (parametersMatch) {
            const [_, playerName, parameterType] = parametersMatch;
            if (parameterType === 'volume') {
                this.audioNodes[playerName].set({
                    'volume': value > -30 ? value: -100,
                });
            } else if (parameterType === 'tone') {
                this.audioNodes[playerName].set({
                    'playbackRate': Math.pow(2,(value/12)),
                });
            } else if (parameterType === 'reverbSend') {
                const nodeName = `${playerName}reverbSend`
                this.audioNodes[nodeName].gain.value = value > -30 ? value: -100
            } else if (parameterType === 'drumkit') {
                const soundName = `${value}-${playerName}`;
                const player = this.audioNodes[playerName];
                const defaultURL = playerConfig[playerName].defaultSampleURL;
                getSampleLibraryManagerInstance().getBufferForSound(soundName, buffer => player.buffer = buffer, defaultURL);
            }
        } else if (parameterName == 'lpf_cutoff'){
            this.audioNodes.cutoff.frequency.rampTo(limitToNyquist(value), 0.01);
        }
    }

    launchSound(playerName, time, normVelocity=1) {
        const release = this.getPlaybackParameterValue(`${playerName}_release`)
        const duration = release * this.getStepDuration() * 2; // dirty hack to make longer samples play for longer
        if (!this.audioNodes[playerName].buffer.loaded) {
            console.warn(`Audio buffer for player "${playerName}" still not loaded, aborting sound launch`);
            return;
        }
        const baseVolume = this.getPlaybackParameterValue(`${playerName}_volume`);
        const volume = Tone.gainToDb(Tone.dbToGain(baseVolume) * normVelocity);
        this.audioNodes[playerName].volume.setValueAtTime(volume, time);
        this.audioNodes[playerName].start(time, 0, duration);
    }

    onSequencerStep(currentMainSequencerStep, time) {
        if (this.getPlaybackParameterValue("clipNumber") === undefined) return;
        // Check if sounds should be played in the current step and do it
        const currentStep = currentMainSequencerStep % (this.getPlaybackClip().length);
        const LOOKAHEAD_SWING = true; // swinged third 16th note is handled in the second one
        if (LOOKAHEAD_SWING && currentStep % 4 === 2) return; 
        const sequence = this.getPlaybackClip().sequence;

        const generalSwing = getAudioGraphInstance().getSwing();
        const stepDuration = this.getStepDuration();
        this.playerNames.forEach(playerName => {
            const pitch = this.getPlayerPitch(playerName);
            const targetProperties = {pitch, onset: currentStep};
            const note = findNoteInSequence(sequence, targetProperties);
            if (note != null) this.launchSound(playerName, time, (note.velocity ?? 127) / 127);
            if (LOOKAHEAD_SWING && (currentStep + 1) % 4 === 2) {
                const targetProperties = {pitch, onset: currentStep + 1};
                const nextNote = findNoteInSequence(sequence, targetProperties);
                if (nextNote != null) {
                    const instrumentSwing = this.getPlaybackParameterValue(`${playerName}_swing`);
                    const totalSwing = generalSwing + instrumentSwing;
                    const delay = stepDuration * 2 * totalSwing;
                    this.launchSound(playerName, time + delay, (nextNote.velocity ?? 127) / 127);
                }
            }
        });
    }
    onMidiNote (pitch, midiVelocity=127, noteOff, extras){
        if (!getAudioGraphInstance().isGraphBuilt()) return;
        
        const playerName = [...Object.entries(playerConfig)].find(([_name, {midiPitch}]) => midiPitch === pitch)?.[0];

        if (!playerName) return;
        if (noteOff) return;
  
        this.launchSound(playerName, undefined, midiVelocity / 127);
        const recEnabled = !extras.skipRecording && this.getPlaybackParameterValue('isRecordArmed');
        // Si Rec està ON
        if (recEnabled) {   
            const currentMainSequencerStep = getAudioGraphInstance().getTransportCurrentStep();
            const currentStep = currentMainSequencerStep % this.getPlaybackClip().length;
            const sequence = structuredClone(staticSelect(this.getDisplaySequenceSelector()));
            const targetProperties = {pitch, onset: currentStep};
            const alreadyExistingNoteIndex = findNoteIndexInSequence(sequence, targetProperties);
            if (alreadyExistingNoteIndex !== -1) sequence.splice(alreadyExistingNoteIndex, 1);
            sequence.push({...targetProperties, duration: 1, velocity: midiVelocity});
            this.updateSequence(sequence);
        };
    }
}